import { BackSide, Color, Data3DTexture, GLSL3, LinearFilter, Mesh, RedFormat, RepeatWrapping, ShaderMaterial, SphereGeometry, Vector3 } from 'three';
import type { CloudRegion } from '../universe/scenery';
import type { ResolvedQuality } from '../types';
import { SeededRandom } from '../core/SeededRandom';

/** Forward ray interval through a unit cloud; also valid when the eye is inside. */
export function cloudInterval(eye: Vector3, direction: Vector3): [number, number] | null {
  const ray = direction.clone().normalize(), b = eye.dot(ray), d = b * b - eye.lengthSq() + 1;
  if (d < 0) return null;
  const end = -b + Math.sqrt(d);
  return end > 0 ? [Math.max(0, -b - Math.sqrt(d)), end] : null;
}

/** A shared, compact 3D noise lattice. Sampling is always in world-fixed cloud coordinates. */
export function createCloudNoise() {
  const size = 64, random = new SeededRandom(8307), data = new Uint8Array(size ** 3);
  for (let i = 0; i < data.length; i++) data[i] = random.int(0, 255);
  const texture = new Data3DTexture(data, size, size, size);
  texture.format = RedFormat; texture.minFilter = texture.magFilter = LinearFilter;
  texture.wrapS = texture.wrapT = texture.wrapR = RepeatWrapping;
  texture.unpackAlignment = 1; texture.needsUpdate = true;
  return texture;
}
const vertex = /* glsl */`
out vec3 vLocal;
void main() { vLocal = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.); }
`;
const fragment = /* glsl */`
precision highp sampler3D;
in vec3 vLocal;
out vec4 outColor;
uniform vec3 uEye, uColor, uAccent;
uniform sampler3D uNoise;
uniform float uSeed, uGalaxy, uProfile, uStrength;
uniform int uSteps;
float n3(vec3 p) { return texture(uNoise, (p + .5) / 64.).r; }
float cloudNoise(vec3 p) {
  return n3(p) * .52 + n3(p * 2.03 + 9.1) * .27 + n3(p * 4.11 + 3.7) * .14 + n3(p * 8.21 + 17.3) * .07;
}
void main() {
  vec3 ray = normalize(vLocal - uEye);
  float b = dot(uEye, ray), d = b * b - dot(uEye, uEye) + 1.;
  if (d < 0.) discard;
  float start = max(0., -b - sqrt(d)), end = -b + sqrt(d);
  if (end <= start) discard;
  float stepSize = (end - start) / float(uSteps);
  float jitter = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898,78.233))) * 43758.5453);
  vec3 color = vec3(0.); float opacity = 0.;
  for (int i = 0; i < 48; i++) {
    if (i >= uSteps || opacity > .96) break;
    vec3 p = uEye + ray * (start + (float(i) + .2 + jitter * .6) * stepSize);
    vec3 warp = vec3(n3(p * 3. + uSeed), n3(p * 4. + 8.3), n3(p * 3. + 24.)) - .5;
    vec3 q = (p + warp * .24) * vec3(7., 9., 48.) + vec3(uSeed, 2.3, 7.1);
    float n = cloudNoise(q), detail = n3(q * 6.);
    float envelope = 1. - smoothstep(.35, 1., length(p));
    float density = smoothstep(.43, .74, n) * envelope;
    vec3 light = mix(uColor, uAccent, smoothstep(.43, .75, n));
    if (uProfile > .5 && uProfile < 1.5) {
      float wave = sin(p.z * 5.5) * .13;
      float h = p.y - wave;
      float banks = exp(-pow((h + .23) * 2.7, 2.)) + .7 * exp(-pow((h - .22) * 3.8, 2.));
      float lane = exp(-pow((h + sin(p.z * 22. + n * 5.) * .055) * 13., 2.));
      float threads = smoothstep(.37, .76, n) * (.45 + detail);
      density = (threads * banks * .45 + lane * .12) * envelope;
      light = mix(uColor * .7, uAccent * .95, smoothstep(.47, .74, n));
      light = mix(light, vec3(.004, .006, .012), lane * .93);
      light = mix(light, vec3(.3, .15, .085), smoothstep(.55, .87, n3(p * 2. + 31.)) * .35);
    } else if (uProfile > 1.5) {
      float shell = exp(-pow((length(p + warp * .06) - .62) * 13., 2.));
      density = shell * (.2 + n * .7) * envelope;
      light = mix(uColor, uAccent, shell) * 1.8;
    }
    if (uGalaxy > .5) {
      float r = length(p.xz), a = atan(p.z, p.x);
      float arm = pow(.5 + .5 * cos(a * 4. - r * 28.8 + n * .6), 3.);
      float core = exp(-r * r * 65.);
      density = ((arm * .6 + .08) * exp(-r * 1.8) * (.35 + n) + core * .45) * exp(-p.y * p.y * 4.) * (1. - smoothstep(.8, 1., r));
      light = mix(vec3(.12, .22, .4), vec3(.95, .63, .34), core);
    }
    float alpha = 1. - exp(-density * stepSize * 3.4 * uStrength);
    color += (1. - opacity) * alpha * light * (.4 + n * .8);
    opacity += (1. - opacity) * alpha;
  }
  if (opacity < .001) discard;
  outColor = vec4(color / max(.001, opacity), opacity);
}
`;

/** Fixed volumes: long dust lanes, filaments, shells and galactic disks share the same entry/exit behaviour. */
export class NebulaVolume {
  readonly mesh: Mesh<SphereGeometry, ShaderMaterial>;
  constructor(readonly destination: CloudRegion, texture = createCloudNoise()) {
    const cloud = destination.nebula ?? { extent: [900, 95, 900] as [number, number, number], accent: '#f4c69d', seed: 182 };
    this.mesh = new Mesh(new SphereGeometry(1, 36, 24), new ShaderMaterial({
      glslVersion: GLSL3, vertexShader: vertex, fragmentShader: fragment, side: BackSide, transparent: true, depthWrite: false,
      uniforms: { uEye: { value: new Vector3() }, uColor: { value: new Color(destination.color) }, uNoise: { value: texture },
        uAccent: { value: new Color(cloud.accent) }, uSeed: { value: cloud.seed }, uGalaxy: { value: destination.kind === 'galaxy' ? 1 : 0 },
        uProfile: { value: destination.profile === 'filament' ? 1 : destination.profile === 'shell' ? 2 : 0 },
        uStrength: { value: destination.strength ?? 1 }, uSteps: { value: 40 } },
    }));
    this.mesh.name = destination.id;
    this.mesh.position.set(...destination.position); this.mesh.scale.set(...cloud.extent);
    if (destination.rotation) this.mesh.rotation.set(...destination.rotation);
    else if (destination.kind === 'galaxy') this.mesh.rotation.z = -.22;
    this.mesh.renderOrder = -5;
  }
  update(eye: Vector3) {
    this.mesh.updateWorldMatrix(true, false);
    this.mesh.material.uniforms.uEye.value.copy(eye);
    this.mesh.worldToLocal(this.mesh.material.uniforms.uEye.value);
  }
  setQuality(quality: ResolvedQuality) { this.mesh.material.uniforms.uSteps.value = quality === 'high' ? 40 : quality === 'medium' ? 28 : 18; }
  get inside() { return this.mesh.material.uniforms.uEye.value.lengthSq() < 1; }
}
